/**
 * Sub-collection nav.
 *
 * Two jobs. First, scroller affordances CSS cannot express — edge fades, page
 * dots, and centring the active pill so you can see where you are after a
 * navigation. Second, the Retour control, which steps the row up one level of
 * the menu tree in place, letting a shopper cross from one branch to another
 * without loading a page in between.
 *
 * Everything is progressive: the current level is server-rendered, Retour is a
 * real link to the level above, and the pills are ordinary anchors. If this
 * module never loads the nav still works, just without the in-place stepping.
 * That is also why it deliberately avoids @theme/component — no dependencies.
 */
class TdmSubcollectionNav extends HTMLElement {
  #frame = null;
  #centre = false;
  #centreBehavior = 'auto';

  connectedCallback() {
    this.list = this.querySelector('.scnav__list');
    if (!this.list) return;

    this.dots = this.querySelector('.scnav__dots');
    this.fadeStart = this.querySelector('.scnav__fade--start');
    this.fadeEnd = this.querySelector('.scnav__fade--end');
    this.tree = null;

    this.onScroll = this.update.bind(this);
    this.list.addEventListener('scroll', this.onScroll, { passive: true });
    this.addEventListener('click', this.#handleClick);

    // ResizeObserver rather than a window resize listener: the list also
    // reflows when the surrounding layout changes without the window moving.
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.list);

    this.update();
    this.centreActive();
  }

  disconnectedCallback() {
    this.list?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('click', this.#handleClick);
    this.observer?.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#centre = false;
  }

  #readTree() {
    const el = this.querySelector('[data-scnav-tree]');
    if (!el) return null;
    try {
      const tree = JSON.parse(el.textContent);
      // The server already rendered this level; track it so Retour knows where
      // it currently is without re-deriving it from the DOM.
      this.path = Array.isArray(tree.path) ? tree.path.slice() : [];
      return tree;
    } catch {
      return null;
    }
  }

  #handleClick = (event) => {
    const up = event.target.closest('[data-scnav-up]');
    if (!up) return;
    this.tree ||= this.#readTree();
    if (!this.tree || !this.path.length) return;

    event.preventDefault();
    this.path.pop();
    this.render();
  };

  /** Mirrors the snippet's handle parsing so both passes agree. */
  #handleFromUrl(url) {
    const parts = String(url || '').split('/collections/');
    if (parts.length < 2) return '';
    return parts.pop().split('/')[0].split('?')[0];
  }

  /** Walks the tree to the node addressed by `path`, or null at root. */
  #nodeAt(path) {
    let node = null;
    let siblings = this.tree.items || [];

    for (const handle of path) {
      const match = siblings.find((item) => item.handle === handle);
      if (!match) return node;
      node = match;
      siblings = match.children || [];
    }
    return node;
  }

  render() {
    const node = this.#nodeAt(this.path);
    const items = node ? node.children || [] : this.tree.items || [];
    if (!items.length) return;

    const scope = node
      ? { title: node.title, url: node.url, handle: node.handle }
      : {
          title: this.tree.allLabel,
          url: this.tree.rootUrl,
          // Derived rather than left empty so the root scope pill can go active
          // on /collections/all, matching what the server renders there.
          handle: this.#handleFromUrl(this.tree.rootUrl),
        };

    const frag = document.createDocumentFragment();

    if (this.path.length) {
      const parent = this.#nodeAt(this.path.slice(0, -1));
      frag.appendChild(
        this.#pill({
          title: this.tree.returnLabel,
          url: parent ? parent.url : this.tree.rootUrl,
          modifier: 'return',
          up: true,
        })
      );
    }

    frag.appendChild(this.#pill({ ...scope, modifier: 'scope' }));

    const sep = document.createElement('li');
    sep.className = 'scnav__sep';
    sep.setAttribute('role', 'presentation');
    sep.setAttribute('aria-hidden', 'true');
    frag.appendChild(sep);

    items.forEach((item) => frag.appendChild(this.#pill(item)));

    this.list.replaceChildren(frag);
    this.list.scrollTo({ left: 0 });
    this.update();
    this.centreActive('smooth');
  }

  #pill({ title, url, handle, children, modifier, up }) {
    const li = document.createElement('li');
    li.className = 'scnav__item';

    const a = document.createElement('a');
    a.className = 'scnav__link';
    if (modifier) a.classList.add(`scnav__link--${modifier}`);
    a.href = url || '#';

    if (up) {
      a.setAttribute('data-scnav-up', '');
      a.innerHTML =
        '<svg class="scnav__back" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      a.append(title || '');
    } else {
      a.textContent = title || '';
    }

    if (children && children.length) a.classList.add('has-children');

    if (handle && handle === this.tree.currentHandle) {
      a.classList.add('is-active');
      a.setAttribute('aria-current', 'page');
    } else if (handle && (this.tree.ancestors || []).includes(handle)) {
      // Stepping up past the current collection would otherwise leave no trace
      // of which branch the shopper came from.
      a.classList.add('is-ancestor');
    }

    li.appendChild(a);
    return li;
  }

  update() {
    if (this.#frame !== null) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      if (this.isConnected) this.#updateFrame();
    });
  }

  #updateFrame() {
    const { list } = this;
    const scrollWidth = list.scrollWidth;
    const clientWidth = list.clientWidth;
    const scrollLeft = list.scrollLeft;
    const overflowing = clientWidth > 0 && scrollWidth > clientWidth + 2;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    const pages = Math.max(1, Math.ceil(scrollWidth / Math.max(1, clientWidth)));
    const overflowChanged = list.classList.contains('is-overflowing') !== overflowing;
    let offset = null;

    if (this.#centre && !overflowChanged) {
      const active = list.querySelector('.is-active');
      if (active && overflowing) {
        const listRect = list.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        offset = activeRect.left - listRect.left + scrollLeft - (clientWidth - activeRect.width) / 2;
      }
      this.#centre = false;
    }

    list.classList.toggle('is-overflowing', overflowing);
    this.fadeStart?.classList.toggle('is-visible', overflowing && scrollLeft > 4);
    this.fadeEnd?.classList.toggle('is-visible', overflowing && scrollLeft < maxScroll - 4);
    this.updateDots(overflowing, pages, maxScroll > 0 ? scrollLeft / maxScroll : 0);

    if (offset !== null) {
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : this.#centreBehavior;
      list.scrollTo({ left: Math.max(0, Math.min(maxScroll, offset)), behavior });
    }
    if (this.#centre) this.update();
  }

  updateDots(overflowing, pages, progress) {
    const { dots } = this;
    if (!dots) return;

    if (!overflowing) {
      dots.hidden = true;
      return;
    }
    dots.hidden = false;

    if (dots.childElementCount !== pages) {
      dots.replaceChildren(
        ...Array.from({ length: pages }, () => {
          const dot = document.createElement('span');
          dot.className = 'scnav__dot';
          return dot;
        })
      );
    }

    const active = Math.round(progress * (pages - 1));

    Array.from(dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === active));
  }

  centreActive(behavior = 'auto') {
    this.#centre = true;
    this.#centreBehavior = behavior;
    this.update();
  }
}

if (!customElements.get('tdm-subcollection-nav')) {
  customElements.define('tdm-subcollection-nav', TdmSubcollectionNav);
}
